// The editor's keyboard: pads (computer keys), Space play/stop, Enter record, N note repeat,
// Cmd/Ctrl+Z undo and Shift+Cmd/Ctrl+Z redo — not while typing or inside the grid (which has its
// own keys). Used by the Write page and the admin Sound Lab.
import { useEffect } from "react";
import { keyVelocity, padForCode } from "../input/keymap";
import { startPlay, startRecording, stopPlay } from "./audio";
import { padDown, padUp } from "./padInput";
import { canRedoNow, canUndoNow, redoEdit, undoEdit, useEditor } from "./store";

export function useEditorKeyboard(): void {
  useEffect(() => {
    const busy = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      return (
        !!t &&
        (t.closest("input, select, textarea, [role=slider], .track-grid, [role=dialog]") !== null ||
          t.isContentEditable)
      );
    };
    const down = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) {
          if (canRedoNow()) redoEdit();
        } else if (canUndoNow()) undoEdit();
        return;
      }
      if (busy(e) || e.metaKey || e.ctrlKey || e.altKey) return;
      const pad = padForCode(e.code);
      if (pad >= 0) {
        e.preventDefault();
        if (!e.repeat) void padDown(pad, keyVelocity(e.shiftKey), "key");
        return;
      }
      if (e.code === "Space" && !(e.target as HTMLElement)?.closest("button")) {
        e.preventDefault();
        const play = useEditor.getState().play;
        void (play === "stopped" ? startPlay("loop") : stopPlay());
      } else if (e.code === "KeyN" && !e.repeat) {
        useEditor.getState().set({ noteRepeat: true });
      } else if (e.code === "Enter" && !(e.target as HTMLElement)?.closest("button, a")) {
        e.preventDefault();
        void (useEditor.getState().play === "recording" ? stopPlay() : startRecording());
      }
    };
    const up = (e: KeyboardEvent) => {
      const pad = padForCode(e.code);
      if (pad >= 0) void padUp(pad, "key");
      if (e.code === "KeyN") useEditor.getState().set({ noteRepeat: false });
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);
}
